/**
 * The auth transport contract. Shared between apps/server and apps/mobile.
 *
 * Identity is Better Auth, self-hosted inside apps/server. Its four tables live
 * in the `auth` schema, and its `id` is a real Postgres uuid, so every person
 * column in `app` and `data` can reference `auth.user.id`.
 *
 * v1 ships one sign-in method: email and password. No social, no phone.
 *
 * Source: docs/records-platform-architecture.md, Section 4a.
 */
import type { MembershipRoleFields } from './roles';
import type { AppLanguageTag } from './agent-language';

/** Better Auth mounts its own handler here. Our own API stays under /v1. */
export const AUTH_BASE_PATH = '/api/auth' as const;

/** Coach's two voices on a live call (S14). */
export const AGENT_VOICES = ['female', 'male'] as const;
export type AgentVoice = (typeof AGENT_VOICES)[number];
export const DEFAULT_AGENT_VOICE: AgentVoice = 'female';
export const isAgentVoice = (value: unknown): value is AgentVoice => typeof value === 'string' && (AGENT_VOICES as readonly string[]).includes(value);

/**
 * How OS opens for a new conversation, the person's choice on Settings › OS's
 * voice: `live`, the default, straight into live mode; `chat`, the chat with
 * Talk live in its composer, which starts live mode only when pressed.
 */
export const OS_OPENS_IN = ['live', 'chat'] as const;
export type OsOpensIn = (typeof OS_OPENS_IN)[number];
export const DEFAULT_OS_OPENS_IN: OsOpensIn = 'live';
export const isOsOpensIn = (value: unknown): value is OsOpensIn => typeof value === 'string' && (OS_OPENS_IN as readonly string[]).includes(value);

/** A signed-in person. Mirrors auth.user, minus anything private. */
export type AuthUser = {
  id: string;
  email: string;
  name: string | null;
  emailVerified: boolean;
  image: string | null;
  createdAt: string;
  /** IANA zone from the device at sign-up, e.g. Asia/Kolkata. Null until a device has sent one. */
  timezone: string | null;
  /** BCP 47 language tag from the device, e.g. en-IN. */
  locale: string | null;
  /** ISO 3166-1 alpha-2 from the device locale, e.g. IN. */
  region: string | null;
  /** ISO 4217 guessed from that device, e.g. INR. Not the business's own currency. */
  localeCurrency: string | null;
  /** Which of Coach's two voices this person hears on a call. Female by default. Coach also speaks and writes of itself in this voice's gender. */
  agentVoice: AgentVoice;
  /**
   * The language the app is in and OS speaks (`APP_LANGUAGES`). Null until the
   * person picks one. Optional because a phone build older than the column
   * serves a cached answer without it; read it as null when absent.
   */
  appLanguage?: AppLanguageTag | null;
  /**
   * How OS opens for a new conversation: live mode (the default) or chat
   * mode. Optional because a held answer from a build older than the column
   * has none; read it as live when absent.
   */
  osOpensIn?: OsOpensIn;
};

/**
 * The most letters a name may have. The invitation sheet holds the same limit
 * (`invite_name_check`), so a name an inviter wrote always fits.
 */
export const MAX_NAME_LENGTH = 120;

/**
 * Whether the person still has to say what they are called (the Get started
 * design, Decided 5). A code that makes a new account leaves the name empty; a
 * provider hands one over, so Google and Outlook never ask. Whitespace is no
 * name. The gate reads this, so the step shows for exactly the accounts that
 * have none, and leaves the moment one is saved.
 */
export const needsName = (user: { name: string | null }): boolean => (user.name ?? '').trim() === '';

/** A name the person typed, trimmed; null when it is empty or longer than `MAX_NAME_LENGTH`. */
export const cleanName = (typed: string): string | null => {
  const name = typed.trim().replace(/\s+/g, ' ');
  return name === '' || name.length > MAX_NAME_LENGTH ? null : name;
};

/** A tenant the signed-in person belongs to. The screen word is "business". */
export type MembershipSummary = {
  tenantId: string;
  tenantName: string;
  /**
   * Where the logo can be fetched, https only. Null on most businesses, and
   * the switcher then draws two letters off `tenantName`. Never fetched by the
   * server: app.tenant.logo_url is opaque to it.
   */
  tenantLogoUrl: string | null;
  /**
   * The level, as a phone already in people's hands reads it. Since
   * web-redesign-02 phase 3 it is derived from the role: the primary admin is
   * `owner`, Admin `admin`, a role that only looks `viewer`, any other role
   * `member` (`authorityOfRole`).
   */
  authority: Authority;
  /**
   * The tenant-scope capabilities this person holds in this business, answered
   * by the server from their role's permissions (`tenantCapabilitiesOf`). The
   * device draws a door when the key is here and never works it out itself.
   * A courtesy, like every grant summary. The server refuses regardless.
   */
  capabilities: TenantCapability[];
  defaultCurrency: string;
  /**
   * The business's own locale (`app.tenant.locale`), BCP 47, e.g. en-IN. A
   * phone field with no saved number and no `default_country` opens on the
   * country it names (`countryOfLocale`). Optional because a cache written
   * before this field has none; read it as unknown when absent.
   */
  locale?: string;
  joinedAt: string;
  /**
   * Whether the role decides what this person may do yet
   * (`app.membership.role_live`). Until it does the level decides, as before
   * roles: `authority` and `capabilities` are the level's, and a door drawn on
   * one of the role's permissions falls back to the level the way the server
   * does (`makesRecords`). A cache written before this field has none, and
   * reads as not live.
   */
  roleLive: boolean;
  /*
   * Roles, beside the level (web-redesign-02, Decided 12): roleId, roleName,
   * isPrimary, permissions and reportsTo. A phone already in people's hands
   * reads `authority` and `capabilities` and never these; a newer client reads
   * these. The server reads them off the membership's role; a membership with
   * no role stands for its level (`roleFieldsOfAuthority`'s mapping).
   */
} & MembershipRoleFields;

/**
 * What a person may do to the business itself. Ours, closed, and seeded by
 * migration 0006 into app.authority. It is not a job title: a tenant names its
 * own roles in app.role, and `authority` only decides who may define one.
 *
 * Ordered high to low. The database holds the same five with a rank, and
 * `db:verify` fails when this list and that table disagree. AppFlowy declares
 * its field type list in two files and the copies have drifted, so their client
 * cannot render two types their core stores.
 */
export const AUTHORITIES = ['owner', 'partner', 'admin', 'member', 'viewer'] as const;
export type Authority = (typeof AUTHORITIES)[number];

/**
 * Every permission the product can grant. Closed, and seeded by 0006 into
 * app.capability, which is the table the role editor renders. A record
 * capability is granted per record through app.record_grant. A tenant
 * capability applies to the whole business and is read from the role alone.
 *
 * `db:verify` fails when these two lists and that table disagree.
 */
export const RECORD_CAPABILITIES = [
  'view_summaries',
  'view_all_entries',
  'export',
  'add_entry',
  'edit_own_entry',
  'edit_any_entry',
  'delete_entry',
  'edit_structure',
] as const;

export const TENANT_CAPABILITIES = [
  'manage_members',
  'define_roles',
  'manage_units',
  'manage_links',
  'manage_policies',
  'close_period',
  'manage_billing',
  /**
   * The business-wide numbers: the Dashboard tab and its insight strip. Seeded
   * by migration 0007 at the partner's rank, so an owner and a partner read
   * them and nobody below does. What a person may total inside one record is
   * still `view_summaries` on that record's grant; this only opens the tab.
   */
  'view_business_insights',
] as const;

export type RecordCapability = (typeof RECORD_CAPABILITIES)[number];
export type TenantCapability = (typeof TENANT_CAPABILITIES)[number];
export type Capability = RecordCapability | TenantCapability;

/**
 * A role, in the tenant's own word. `name` is set on a role the tenant defined
 * and `labelKey` on one we ship, never both, so there is one answer to what it
 * is called. A rename never bumps `version`; a permission change always does.
 */
export type Role = {
  id: string;
  name: string | null;
  labelKey: string | null;
  version: number;
  minAuthority: Authority;
};

/**
 * GET /v1/me
 *
 * The one call the app makes after a session exists. It answers who the person
 * is and which businesses they may open. An empty `memberships` array means the
 * onboarding flow of PRD Section 6 has not run yet.
 */
export type MeResponse = {
  user: AuthUser;
  memberships: MembershipSummary[];
};
